import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import { getCurrentContext } from '../../../services/current-context';
import type {
  TeamMemberResponse,
  CreateTeamMemberRequest,
  UpdateTeamMemberRequest,
  RoleOption,
  RolePermissionDTO,
  LegacyRole,
} from '../../../types/team.types';

const assignRoles = (
  roleString: string,
  scopeEntity?: string,
): LegacyRole[] => {
  // entityName is the entity the role belongs to. The backend resolves a
  // member's entity from roles[].entityName (see user-service addUser/updateUser),
  // so this must be the member's OWN entity.
  //
  // - Invite/add: no scopeEntity is passed, so we fall back to the inviter's
  //   current context. entityName must be the bare entity, NOT the raw
  //   currentContext: the CurrentContext claim (stored verbatim in localStorage)
  //   is in Keycloak's group-name format `{Entity}{Role}` (e.g. "AcmeCustomerAdmin");
  //   sending it raw makes the backend append the new member's role onto the
  //   inviter's role ("AcmeCustomerAdmin" + "CustomerUser"). getCurrentContext()
  //   strips the role suffix so the new member is scoped to the entity only.
  // - Edit: callers pass the member's resolved entity as scopeEntity so a role
  //   change keeps a child-entity member on their child entity instead of
  //   reassigning them to the admin's (parent) current context (DV-2301).
  const entityName = scopeEntity?.trim() || getCurrentContext() || null;
  return roleString
    .split(',')
    .map((role) => role.trim())
    .map((role) => ({
      role,
      entityUuid: null,
      entityName,
    }));
};

export const teamApi = createApi({
  reducerPath: 'teamApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['TeamMembers', 'Roles'],
  endpoints: (builder) => ({
    getTeamMembers: builder.query<TeamMemberResponse[], void>({
      query: () => '/api/v1/users/external',
      providesTags: ['TeamMembers'],
    }),

    addTeamMember: builder.mutation<TeamMemberResponse, CreateTeamMemberRequest>({
      query: (body) => ({
        url: '/api/v1/users/external',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamMembers'],
    }),

    updateTeamMember: builder.mutation<
      TeamMemberResponse,
      UpdateTeamMemberRequest
    >({
      query: ({ userId, profileStatus, ...body }) => ({
        url: `/api/v1/users/external/${userId}`,
        method: 'PUT',
        body: {
          ...body,
          userId,
          isActive: profileStatus !== 'Inactive',
        },
      }),
      invalidatesTags: ['TeamMembers'],
    }),

    resendInvite: builder.mutation<string, string>({
      query: (userId) => ({
        url: `/api/v1/users/${userId}/execute-actions-email`,
        method: 'PUT',
      }),
    }),

    getRoles: builder.query<RoleOption[], void>({
      query: () => '/api/v1/roles/external',
      providesTags: ['Roles'],
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        localStorage.setItem('userRoles', JSON.stringify(data));
      },
    }),

    getRolesPermissions: builder.query<RolePermissionDTO, void>({
      query: () => '/api/v1/roles/permissions',
    }),
  }),
});

export const {
  useGetTeamMembersQuery,
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useResendInviteMutation,
  useGetRolesQuery,
  useGetRolesPermissionsQuery,
} = teamApi;

/** Format roles for SearchSelect dropdown */
export const getRoleSelectOptions = (
  roles: RoleOption[],
  useDescription = false,
): Array<{ value: string; label: string }> => {
  return roles
    .filter(
      (r) =>
        r.roleDescription !== 'Customer developer' &&
        r.roleDescription !== 'Customer finance',
    )
    .map((r) => ({
      value: useDescription ? r.roleDescription : r.roleCode,
      label: r.roleDescription,
    }));
};

export { assignRoles };
